import React, { useCallback, useMemo, useRef } from "react";
import { useScrollIntoViewIfNotVisible } from "../../../hooks/useScrollIntoViewIfNotVisible";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { RecoverEmailLayout } from "@fmc-digital/ui";
import { ROUTES } from "@/constants/routes.constants";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { useRecoverUsernameSubmit } from "@/hooks/useRecoverUsernameSubmit";
import { useRecoverUsername } from "@/contexts/RecoverUsernameContext";
import { createRecoverUsernameValidationSchema } from "@/utils/recover-username.utils";
import type { FormValues } from "./recover-email-form.types";

/**
 * Auth-shell adapter for forgot-email personal info: validation, API, i18n → {@link RecoverEmailLayout}.
 */
export const RecoverEmailForm: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { setEmail } = useRecoverUsername();
  const { t } = useTranslation();
  const getErrorMessage = useApiErrorMessage();

  const onSuccessExtra = useCallback(
    (response: { factorsList?: unknown }) => {
      let factorsList: Array<{ profile?: { credentialId?: string } }> = [];
      if (Array.isArray(response.factorsList)) {
        factorsList = response.factorsList;
      } else if (response.factorsList) {
        factorsList = [response.factorsList];
      }
      const emailFromResponse = factorsList
        .map((factor) => factor?.profile?.credentialId)
        .find((credentialId) => credentialId);
      if (emailFromResponse) {
        setEmail(emailFromResponse.trim().toLowerCase());
      }
    },
    [setEmail],
  );

  const { onSubmit, isLoading, error, responseBodyError } =
    useRecoverUsernameSubmit(onSuccessExtra);

  const validationSchema = useMemo(
    () =>
      createRecoverUsernameValidationSchema({
        validLastName: t("errors.validLastName"),
        validSsn: t("errors.validSsn"),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { lastName: "", ssn: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleCancel = useCallback(() => {
    form.reset();
    navigate(ROUTES.LOGIN);
  }, [form, navigate]);

  const watchedValues = form.watch();
  const hasRequiredValues =
    (watchedValues.lastName?.trim() ?? "").length >= 2 &&
    (watchedValues.ssn?.replace(/\D/g, "") ?? "").length === 9;
  const isFormValid =
    form.formState.isValid && hasRequiredValues && !isLoading;

  const handleNext = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleFormSubmit = useCallback(
    (e: React.SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      form.handleSubmit(onSubmit)(e);
    },
    [form, onSubmit],
  );

  const errorBannerRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewIfNotVisible(errorBannerRef, !!(error || responseBodyError));

  const copy = useMemo(
    () => ({
      title: t("auth.forgotEmail.personalInfo.title"),
      description: t("auth.forgotEmail.personalInfo.description"),
      lastNameLabel: t("auth.forgotEmail.personalInfo.lastNameLabel"),
      lastNamePlaceholder: t("auth.forgotEmail.personalInfo.lastNamePlaceholder"),
      next: t("auth.forgotEmail.buttons.next"),
      cancel: t("auth.forgotEmail.buttons.cancel"),
    }),
    [t],
  );

  const resolvedError =
    error || responseBodyError ? getErrorMessage(error || responseBodyError) : null;

  return (
    <RecoverEmailLayout
      copy={copy}
      form={form}
      onFormSubmit={handleFormSubmit}
      onNext={handleNext}
      onCancel={handleCancel}
      isLoading={isLoading}
      isSubmitDisabled={!isFormValid}
      error={resolvedError}
      errorBannerRef={errorBannerRef}
    />
  );
};
